import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { getAgreementByPublicId, getContactPersonForAgreement, updateAgreement, getAccountTypesByAgreement } from "../../api/Agreement"; // Import getContactPersonForAgreement
import toast from 'react-hot-toast';
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import AgreementForm from "../../components/agreement/AgreementForm";
import ContactPerson from "../../components/ContactPerson";
import Breadcrumbs from "../../components/Breadcrumbs";
import AgreementServices from "../../components/agreement/AgreementServices";
import TableView from "../../components/TableView";
import priceFormatter from "../../helpers/PriceFormatter";

const EditAgreementsPage = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { getAccessToken } = useAuth();
  const { agreementPublicId } = useParams(); // Ensure this matches the route parameter
  const navigate = useNavigate();
  const [agreement, setAgreement] = useState(null);
  const [contactPerson, setContactPerson] = useState(null); // Add state for contact person
  const [loadingAgreement, setLoadingAgreement] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [activeTab, setActiveTab] = useState(query.get("tab") ? query.get("tab") : "services");
  const [accountTypes, setAccountTypes] = useState([]);

  useEffect(() => {
    const tab = query.get("tab");
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    query.set("tab", activeTab);
    navigate(`${location.pathname}?${query.toString()}`, { replace: true });
  }, [activeTab, location.pathname, navigate]);

  useEffect(() => {
    if (agreementPublicId) {
      handleGetAgreement();
    }
  }, [agreementPublicId]);

  const handleGetAgreement = async () => {
    try {
      const accessToken = await getAccessToken();
      const newAgreement = await getAgreementByPublicId(agreementPublicId, accessToken); // Fetch the agreement with API call
      setAgreement(newAgreement);

      const newContactPerson = await getContactPersonForAgreement(agreementPublicId, accessToken); // Fetch the contact person with API call
      setContactPerson(newContactPerson);

      const newAccountTypes = await getAccountTypesByAgreement(agreementPublicId, accessToken);
      newAccountTypes.forEach(type => {
        type.price = priceFormatter(type.price);
      });
      setAccountTypes(newAccountTypes);

      setLoadingAgreement(false);
    } catch (error) {
      console.error("Failed to fetch agreement or contact person:", error);
      setLoadingAgreement(false);
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({ ...s, isOpen: true, title: "Discard changes?", confirm: () => { navigate("/agreements") } }));
    } else {
      navigate("/agreements");
    }
  };

  const saveAndExit = () => {
    handleUpdateAgreement();
  };

  const handleUpdateAgreement = async () => {
    const toastId = toast.loading("Updating agreement");
    try {
      const accessToken = await getAccessToken();
      await updateAgreement(agreement, accessToken);
      toast.success("Agreement updated", { id: toastId });
      setHasChanges(false);
    } catch (error) {
      toast.error("Updating agreement failed", { id: toastId });
      console.error(error);
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage title={"Agreements"} loading={loadingAgreement} />
          </div>
          <div className={`${loadingAgreement ? "loading" : ""} page-content`}>
            <Breadcrumbs />
            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                Agreement
              </h2>
              <div className="flex gap-4 ml-auto">
                <button className="btn btn-outline-primary hidden" onClick={handleBackClick}>
                  Cancel
                </button>
                <button className="btn btn-primary hidden" onClick={saveAndExit}>
                  Save
                </button>
              </div>
            </div>

            <div className="border-b border-mute-200 mb-4 gap-4 flex">
              <button
                className={`px-4 pb-2 ${activeTab === "services"
                  ? "border-b-2 border-black font-bold text-sm text-black"
                  : "text-mute-400 text-sm font-bold"
                  }`}
                onClick={() => setActiveTab("services")}
              >
                Services
              </button>
              <button
                className={`px-4 pb-2 ${activeTab === "accountTypes"
                  ? "border-b-2 border-black font-bold text-sm text-black"
                  : "text-mute-400 text-sm font-bold"
                  }`}
                onClick={() => setActiveTab("accountTypes")}
              >
                Account types
              </button>
              <button
                className={`px-4 pb-2 ${activeTab === "team"
                  ? "border-b-2 border-black font-bold text-sm text-black"
                  : "text-mute-400 text-sm font-bold"
                  }`}
                onClick={() => setActiveTab("team")}
              >
                Team
              </button>
              {agreement?.supportManualUrl &&
                <button
                  className={`px-4 pb-2 ${activeTab === "supportManual"
                    ? "border-b-2 border-black font-bold text-sm text-black"
                    : "text-mute-400 text-sm font-bold"
                    }`}
                  onClick={() => setActiveTab("supportManual")}
                >
                  Support manual
                </button>
              }
              
             {/* <button
                className={`px-4 pb-2 ${activeTab === "details"
                  ? "border-b-2 border-black font-bold text-sm text-black"
                  : "text-mute-400 text-sm font-bold"
                  }`}
                onClick={() => setActiveTab("details")}
              >
                Details
              </button>*/}
            </div>

            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              {activeTab == "details" && <AgreementForm
                agreement={agreement || {}}
                setAgreement={setAgreement}
                setHasChanges={setHasChanges}
                disabled={true}
              />}

              {activeTab == "services" && <AgreementServices agreement={agreement} />}

              {activeTab == "team" &&
                <div className="flex flex-row gap-8">
                  <div>
                    <h5 className="text-lg font-bold mb-6">Diflex</h5>
                    <div className="flex flex-col gap-4">
                      {agreement?.diflexContacts?.map((member) => {
                        return (<ContactPerson
                          key={member.contactPersonPublicId}
                          bgColor="bg-primary-100"
                          contactPerson={member}
                        />)
                      })}
                    </div>
                  </div>
                  <div>
                    {agreement?.customerContacts?.length > 0 && <h5 className="text-lg font-bold mb-6">Internal</h5>}
                    
                    <div className="flex flex-col gap-4">
                      {agreement?.customerContacts?.map((member) => {
                        return (<ContactPerson
                          key={member.contactPersonPublicId}
                          contactPerson={member}
                        />)
                      })}
                    </div>
                  </div>
                </div>
              }

              {activeTab == "supportManual" &&
                <div className="w-full">
                  <h5 className="text-lg font-bold mb-6">Support manual</h5>
                  <a className="text-sm text-primary-500 mb-4" href={agreement?.supportManualUrl} target="_blank" rel="noopener noreferrer">Open support manual in a new tab</a>
                </div>
              }
              
              {activeTab == "accountTypes" &&
                <TableView
                  data={accountTypes}
                  selectedRows={accountTypes} // Map selectedUsers to selectedRows
                  setSelectedRows={setAccountTypes} // Ensure selection updates correctly
                  noCheckbox={true}
                  showActions={false}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "price", label: "Price" },
                    { key: "status", label: "Status" },
                    { key: "comment", label: "Comment" },
                  ]}
                  primaryKey="accountTypePublicId" // Match this with accountTypePublicId
                  basePath={'#'} // Match this with the path to edit account type
/*                     handleDelete={handleDeleteAccountType}
*/                  />
              }
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title ? confirmState.title : "Discard changes?"}
          onClose={() => { setConfirmState((s) => ({ ...s, isOpen: false })) }}
          onConfirm={() => { setConfirmState((s) => ({ ...s, isOpen: false })); confirmState.confirm() }}
        />
      </div>
    </>
  );
};

export default EditAgreementsPage;
